import axios from 'axios';

export interface PaljaCatalogProduct {
  id: string;
  title: string;
  description: string;
  priceWon: number;
  pageCount: number;
  previewPageCount: number;
  coverStyle: string;
  coverUrl: string;
  active: boolean;
  sortOrder: number;
  available: boolean;
  unavailableReason: string | null;
  revision: number;
  updatedAt: string;
}

export interface PaljaCatalogDraft {
  title: string;
  description: string;
  active: boolean;
  sortOrder: number;
}

const API = '/api/palja/admin/books/products';
export const PALJA_COVER_MAX_BYTES = 8 * 1024 * 1024;

export class PaljaCatalogError extends Error {
  constructor(message: string, public readonly status = 0, public readonly code = '') {
    super(message);
    this.name = 'PaljaCatalogError';
  }
}

function record(value: unknown): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new PaljaCatalogError('책 정보의 응답 형식이 올바르지 않습니다.');
  }
  return value as Record<string, unknown>;
}

export function parsePaljaCatalogProduct(value: unknown): PaljaCatalogProduct {
  const product = record(value);
  for (const key of ['id', 'title', 'description', 'coverStyle', 'coverUrl', 'updatedAt']) {
    if (typeof product[key] !== 'string') {
      throw new PaljaCatalogError(`책 정보의 ${key} 값이 올바르지 않습니다.`);
    }
  }
  for (const key of ['priceWon', 'pageCount', 'previewPageCount', 'sortOrder', 'revision']) {
    if (!Number.isSafeInteger(product[key])) {
      throw new PaljaCatalogError(`책 정보의 ${key} 값이 올바르지 않습니다.`);
    }
  }
  if (typeof product['active'] !== 'boolean' || typeof product['available'] !== 'boolean') {
    throw new PaljaCatalogError('책 정보의 판매 상태가 올바르지 않습니다.');
  }
  if (product['unavailableReason'] !== null && typeof product['unavailableReason'] !== 'string') {
    throw new PaljaCatalogError('책 정보의 판매 안내가 올바르지 않습니다.');
  }
  if (!/^\/api\/palja\/books\/covers\/[a-f0-9]{64}\.png$/.test(product['coverUrl'] as string)) {
    throw new PaljaCatalogError('책 표지 주소가 올바르지 않습니다.');
  }
  return product as unknown as PaljaCatalogProduct;
}

export function catalogDraft(product: PaljaCatalogProduct): PaljaCatalogDraft {
  return { title: product.title, description: product.description, active: product.active, sortOrder: product.sortOrder };
}

export function validatePaljaCatalogDraft(draft: PaljaCatalogDraft): string | null {
  if (!draft.title.trim() || draft.title.trim().length > 100) return '제목은 1~100자로 입력하세요.';
  if (!draft.description.trim() || draft.description.trim().length > 1000) return '설명은 1~1,000자로 입력하세요.';
  if (!Number.isInteger(draft.sortOrder) || draft.sortOrder < 0 || draft.sortOrder > 9999) {
    return '정렬 순서는 0~9,999 사이 정수로 입력하세요.';
  }
  return null;
}

export function validatePaljaCover(file: Pick<File, 'type' | 'size'>): string | null {
  if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) return 'PNG, JPG, WebP 이미지를 선택하세요.';
  if (file.size === 0 || file.size > PALJA_COVER_MAX_BYTES) return '표지 파일은 0바이트보다 크고 8MB 이하여야 합니다.';
  return null;
}

function requestConfig(token: string) {
  if (!token) throw new PaljaCatalogError('관리자 계정으로 로그인하세요.', 401);
  return { headers: { Authorization: `Bearer ${token}` }, withCredentials: true };
}

export function paljaCatalogError(error: unknown): PaljaCatalogError {
  if (error instanceof PaljaCatalogError) return error;
  if (axios.isAxiosError(error)) {
    const status = error.response?.status ?? 0;
    const body = error.response?.data;
    if (typeof body === 'object' && body !== null) {
      const code = typeof body.code === 'string' ? body.code : '';
      const detail = typeof body.detail === 'string' ? body.detail : '요청을 처리하지 못했습니다.';
      if (status === 409) return new PaljaCatalogError('다른 관리자가 이 책을 변경했습니다. 최신 정보를 확인하세요.', status, code);
      if (status === 401) return new PaljaCatalogError('로그인이 만료되었습니다. 다시 로그인하세요.', status, code);
      if (status === 403) return new PaljaCatalogError('책을 관리할 권한이 없습니다.', status, code);
      return new PaljaCatalogError(detail, status, code);
    }
    return new PaljaCatalogError(status ? '서버 요청에 실패했습니다.' : '서버에 연결하지 못했습니다. 다시 시도하세요.', status);
  }
  return new PaljaCatalogError(error instanceof Error ? error.message : '요청을 처리하지 못했습니다.');
}

export async function getPaljaCatalog(token: string, signal?: AbortSignal): Promise<PaljaCatalogProduct[]> {
  const response = await axios.get<unknown>(API, { ...requestConfig(token), signal });
  const body = record(response.data);
  if (!Array.isArray(body['products'])) throw new PaljaCatalogError('책 목록의 응답 형식이 올바르지 않습니다.');
  return body['products'].map(parsePaljaCatalogProduct);
}

export async function savePaljaCatalogProduct(token: string, productId: string, draft: PaljaCatalogDraft, revision: number): Promise<PaljaCatalogProduct> {
  const validation = validatePaljaCatalogDraft(draft);
  if (validation) throw new PaljaCatalogError(validation);
  const response = await axios.put<unknown>(`${API}/${encodeURIComponent(productId)}`, {
    ...draft, title: draft.title.trim(), description: draft.description.trim(), revision,
  }, requestConfig(token));
  return parsePaljaCatalogProduct(record(response.data)['product']);
}

export async function uploadPaljaCover(token: string, productId: string, file: File, revision: number): Promise<PaljaCatalogProduct> {
  const validation = validatePaljaCover(file);
  if (validation) throw new PaljaCatalogError(validation);
  const form = new FormData();
  form.append('file', file);
  form.append('revision', String(revision));
  const response = await axios.post<unknown>(`${API}/${encodeURIComponent(productId)}/cover`, form, requestConfig(token));
  return parsePaljaCatalogProduct(record(response.data)['product']);
}
